import woodBlock from '@tunasong/media/audio/woodblock.wav'
import type { Rhythm } from '@tunasong/models'
import { useEffect, useRef, useState } from 'react'
import { Player } from 'tone'
import { useAudioEngine } from '../hooks/index.js'

/**
 * Metronome backed by the audio engine.
 *
 * The engine initializes its audio stack lazily (GH #1542). Mounting a
 * metronome button must therefore NOT touch `engine.transport` or
 * `engine.mixer` — the transport schedule, mixer channel, and sample player
 * are created only when the metronome starts (a user gesture).
 */
export const useMetronome = (rhythm?: Rhythm) => {
  const engineRef = useRef(useAudioEngine())

  const [playing, setPlaying] = useState(false)

  /** Tik / tok - use this to trigger when the metronome triggers */
  const [tik, setTik] = useState(false)

  /** Latest rhythm, applied when the metronome starts (and live while playing) */
  const rhythmRef = useRef(rhythm)
  useEffect(() => {
    rhythmRef.current = rhythm
    if (playing && rhythm) {
      engineRef.current.rhythm = rhythm
    }
  }, [playing, rhythm])

  useEffect(() => {
    if (!playing) {
      return
    }

    /** First audio access - lazily initializes the engine's audio stack */
    const engine = engineRef.current
    const { transport, mixer } = engine
    if (rhythmRef.current) {
      engine.rhythm = rhythmRef.current
    }

    const player = new Player(woodBlock)

    const metronomeEv = transport.scheduleRepeat(time => {
      /** Skip ticks that fire before the woodblock sample finishes loading.
       * Tone's ToneBufferSource asserts buffer.loaded inside start() and
       * throws "buffer is either not set or not loaded" otherwise (TUNASONG-VD).
       */
      if (!player.loaded) {
        return
      }
      player.start(time)

      /** Visual metronome support (tik / tok) */
      engine.draw.schedule(() => {
        setTik(tik => !tik)
      }, time)
    }, '4n')

    /** For custom context(s) we don't send output to the speakers. Clients will use this.output instead */
    mixer.addChannel(
      {
        id: 'metronome',
        gain: 1.0,
        inputNode: player.output as never,
        name: 'metronome',
      },
      'media'
    )

    transport.start()

    return () => {
      mixer.removeChannel('metronome')
      transport.clear(metronomeEv)
      transport.stop()
    }
  }, [playing])

  const start = () => setPlaying(true)

  const stop = () => setPlaying(false)

  const toggle = () => setPlaying(current => !current)

  return { start, stop, toggle, playing, tik: playing ? tik : false, tok: playing ? !tik : false }
}
